import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedTime } from 'react-intl';
import classNames from 'classnames';
import OverlayTrigger from '@rio-cloud/rio-uikit/lib/es/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/lib/es/Tooltip';
import ShortBaseSwVersion from '~/features/baseSwVersions/components/ShortBaseSwVersion';
import UpdateEventType from '~/features/updateEvents/components/UpdateEventType';
import {
    DOWNLOAD_FINISHED,
    DOWNLOAD_IN_PROGRESS,
    FAILED,
    INSTALLATION_FINISHED,
    INSTALLATION_IN_PROGRESS,
    REBOOT_PENDING,
    rolloutStatus,
    SENT_TO_DEVICE,
    SUCCESS,
    updateOutcome,
} from '~/features/devices/constants/deviceRolloutStates';

interface RolloutState {
    type: string;
    isFinished: boolean;
    outcome?: string;
    eventSteps: Array<{ state: string; info?: string; timestamp?: number }>;
    lastUpdated: number;
    deliverableId: string;
    version: string;
    subProgress?: RolloutState[];
}

interface SubProgressState {
    percentage: number;
    iconClass: string | null;
    tooltipId: string | null;
}

interface Props {
    subProgress: RolloutState[];
}

const PROGRESS_STATES = {
    COMPLETED: {
        iconClass: updateOutcome[SUCCESS].className,
        tooltipId: updateOutcome[SUCCESS].tooltip,
    },
    FAILED: {
        iconClass: updateOutcome[FAILED].className,
        tooltipId: updateOutcome[FAILED].tooltip,
    },
    IN_PROGRESS: {
        iconClass: 'rioglyph rioglyph-refresh spinning text-color-warning',
        tooltipId: 'intl-msg:deviceUpdateOutcome.ongoing',
    },
    WAITING: {
        iconClass: 'rioglyph rioglyph-time text-color-gray',
        tooltipId: 'intl-msg:waitingForDownload',
    },
} as const;

const DeviceRolloutSubProgress: React.FC<Props> = ({ subProgress }) => {
    const [progressStates, setProgressStates] = useState<Map<number, SubProgressState>>(new Map());
    const [hasMultipleDownloads, setHasMultipleDownloads] = useState(false);

    const getLastStepIndex = (steps: Array<{ state: string }>, state: string) => 
        steps.map((s, i) => s.state === state ? i : -1).filter(i => i !== -1).pop();

    const getDownloadSteps = (subEvent: RolloutState) => {
        const lastInProgressIdx = getLastStepIndex(subEvent.eventSteps, DOWNLOAD_IN_PROGRESS);
        const lastFinishedIdx = getLastStepIndex(subEvent.eventSteps, DOWNLOAD_FINISHED);

        let downloadFinished = null;
        let downloadInProgress = null;

        if (lastInProgressIdx !== undefined && lastFinishedIdx !== undefined) {
            if (lastFinishedIdx > lastInProgressIdx) {
                downloadFinished = subEvent.eventSteps[lastFinishedIdx];
                downloadInProgress = subEvent.eventSteps[lastInProgressIdx];
            } else {
                downloadInProgress = subEvent.eventSteps[lastInProgressIdx];
            }
        } else if (lastFinishedIdx !== undefined) {
            downloadFinished = subEvent.eventSteps[lastFinishedIdx];
        } else if (lastInProgressIdx !== undefined) {
            downloadInProgress = subEvent.eventSteps[lastInProgressIdx];
        }

        return { downloadFinished, downloadInProgress };
    };

    const parsePercentage = (info?: string) => {
        if (!info) return 0;
        const parsed = parseInt(info, 10);
        return isNaN(parsed) ? 0 : parsed;
    };

    const calculateProgressState = (subEvent: RolloutState): SubProgressState => {
        const { downloadFinished, downloadInProgress } = getDownloadSteps(subEvent);
        const sentToDevice = subEvent.eventSteps.find(step => step.state === SENT_TO_DEVICE);
        const hasInstallationStep = subEvent.eventSteps.some(step =>
            step.state === INSTALLATION_IN_PROGRESS ||
            step.state === REBOOT_PENDING ||
            step.state === INSTALLATION_FINISHED
        );

        // Priority 1: Download Completed
        if (downloadFinished || hasInstallationStep || subEvent.outcome === SUCCESS) {
            return { percentage: 100, ...PROGRESS_STATES.COMPLETED };
        }
        // Priority 2: Failed State
        if (subEvent.isFinished && subEvent.outcome === FAILED) {
            return { percentage: parsePercentage(downloadInProgress?.info), ...PROGRESS_STATES.FAILED };
        }
        // Priority 3: Download In Progress
        if (downloadInProgress) {
            return { percentage: parsePercentage(downloadInProgress.info), ...PROGRESS_STATES.IN_PROGRESS };
        }
        // Priority 4: Waiting for Download
        if (sentToDevice) {
            return { percentage: 0, ...PROGRESS_STATES.WAITING };
        }

        return { percentage: 0, iconClass: null, tooltipId: null };
    };

    useEffect(() => {
        const newStates = new Map<number, SubProgressState>();
        let foundMultipleDownloads = false;

        subProgress.forEach((subEvent, index) => {
            const downloadSteps = subEvent.eventSteps.filter(step => step.state === DOWNLOAD_IN_PROGRESS);
            if (downloadSteps.length > 1) foundMultipleDownloads = true;

            newStates.set(index, calculateProgressState(subEvent));
        });

        setProgressStates(newStates);
        setHasMultipleDownloads(foundMultipleDownloads);
    }, [subProgress]);

    const renderSubProgressItem = (subEvent: RolloutState, index: number) => {
        const state = progressStates.get(index);
        if (!state) return null;

        const { percentage, iconClass, tooltipId } = state;

        // Get the last download_in_progress and download_finished indices for tooltip
        const lastDownloadInProgressIndex = subEvent.eventSteps.map((s, i) => s.state === DOWNLOAD_IN_PROGRESS ? i : -1).filter(i => i !== -1).pop();
        const lastDownloadFinishedIndex = subEvent.eventSteps.map((s, i) => s.state === DOWNLOAD_FINISHED ? i : -1).filter(i => i !== -1).pop();

        let stepsToShow: Array<{ state: string; info?: string; timestamp?: number }> = [];

        // If we have both, check which one is last
        if (lastDownloadInProgressIndex !== undefined && lastDownloadFinishedIndex !== undefined) {
            if (lastDownloadFinishedIndex > lastDownloadInProgressIndex) {
                // download_finished is after download_in_progress, show both
                stepsToShow = [subEvent.eventSteps[lastDownloadInProgressIndex], subEvent.eventSteps[lastDownloadFinishedIndex]];
            } else {
                // download_in_progress is after download_finished, show only in_progress
                stepsToShow = [subEvent.eventSteps[lastDownloadInProgressIndex]];
            }
        } else if (lastDownloadFinishedIndex !== undefined) {
            stepsToShow = [subEvent.eventSteps[lastDownloadFinishedIndex]];
        } else if (lastDownloadInProgressIndex !== undefined) {
            stepsToShow = [subEvent.eventSteps[lastDownloadInProgressIndex]];
        }

        const eventStepsTooltip = (
            <Tooltip id='tooltip' className='top-right'>
                <div className='text-left'>
                    {stepsToShow.map((step, i) => (
                        <div key={i} className='padding-bottom-2'>
                            <FormattedMessage id={rolloutStatus[step.state]?.label || 'intl-msg:unknown'} />
                            {step.timestamp && (
                                <span className='padding-left-5'>
                                    <FormattedTime value={step.timestamp} hour='numeric' minute='numeric' second='numeric' />
                                </span>
                            )}
                            {step.info && (
                                <div className='text-color-gray'>{step.info}</div>
                            )}
                        </div>
                    ))}
                </div>
            </Tooltip>
        );

        return (
            <div key={index} className='padding-left-25 padding-right-10 padding-top-5 padding-bottom-5'>
                <div className='display-flex align-items-center gap-10 padding-bottom-5'>
                    <UpdateEventType value={subEvent.type} />
                    <div className='text-color-dark text-size-12'>
                        {subEvent.deliverableId} - v.<ShortBaseSwVersion baseSwVersion={subEvent.version} />
                    </div>
                </div>
                <div className='display-flex align-items-center gap-10 padding-top-5'>
                    <div className='progress margin-bottom-0 width-100pct' style={{ border: '1px solid #ccc', height: '8px' }}>
                        <div className='progress-bar progress-bar-success' style={{ width: `${percentage}%` }} />
                    </div>
                    <OverlayTrigger placement='top' overlay={eventStepsTooltip}>
                        <span className='text-size-12 white-space-nowrap' style={{ cursor: 'pointer' }}>{percentage}%</span>
                    </OverlayTrigger>
                    {iconClass && tooltipId && (
                        <span>
                            <OverlayTrigger
                                placement='top'
                                overlay={
                                    <Tooltip id='tooltip' className='top-right'>
                                        <FormattedMessage id={tooltipId} />
                                    </Tooltip>
                                }
                            >
                                <span className={classNames('text-size-18', iconClass)}></span>
                            </OverlayTrigger>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <h4 className='padding-left-25 text-color-dark text-size-12 font-weight-bold padding-bottom-3'>
                <FormattedMessage id='intl-msg:downloadProgress' />
            </h4>
            {hasMultipleDownloads && (
                <div className='padding-left-25 padding-right-25 padding-top-5 padding-bottom-10'>
                    <div className='alert alert-warning margin-bottom-0 padding-top-10 padding-bottom-15'>
                        <span className='rioglyph rioglyph-warning-sign margin-right-5'></span>
                        <span className='text-size-12'>
                            <FormattedMessage id='intl-msg:multipleDownloadEventsWarning' defaultMessage='This device software version does not allow individual progress tracking per deliverable. As a result, the displayed progress data may be inaccurate.' />
                        </span>
                    </div>
                </div>
            )}
            {subProgress.map((subEvent, subIndex) =>
                renderSubProgressItem(subEvent, subIndex)
            )}
        </>
    );
};

export default DeviceRolloutSubProgress;
